import html2canvas from "html2canvas";
export const PDF_PAGE_WIDTH = 940;
export const PDF_PAGE_HEIGHT = 1400;
export const clampPdfPage = (page: number, count: number) => Math.max(0, Math.min(Math.max(0, count - 1), Number.isFinite(page) ? page : 0));
const frameTick = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
const bounded = async <T,>(promise: Promise<T>, milliseconds = 20000): Promise<T> => {
  let timer: number;
  try { return await Promise.race([promise, new Promise<T>((_, reject) => { timer = window.setTimeout(() => reject(new Error("PDF resources timed out")), milliseconds); })]); }
  finally { clearTimeout(timer!); }
};
async function ready(root: HTMLElement) {
  const doc = root.ownerDocument;
  await bounded(Promise.all(Array.from(root.querySelectorAll("img")).map(async image => {
    if (!image.complete) await new Promise<void>(resolve => { image.addEventListener("load", () => resolve(), { once: true }); image.addEventListener("error", () => resolve(), { once: true }); });
    if (image.decode && image.naturalWidth) await image.decode().catch(() => {});
  })));
  if (doc.fonts?.ready) await bounded(doc.fonts.ready);
}
export type PdfDocumentSession = { pages: readonly HTMLElement[]; capture: (page: HTMLElement, signal?: AbortSignal) => Promise<HTMLCanvasElement>; dispose: () => void };
// Snapshot once into an independent desktop-width document; navigation only selects pages.
export async function createPdfDocumentSession(source: HTMLElement, scale = 1.5): Promise<PdfDocumentSession> {
  await ready(source); await frameTick(); await frameTick();
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  Object.assign(host.style, { position: "fixed", width: "0", height: "0", overflow: "hidden", pointerEvents: "none", left: "0", top: "0" });
  const frame = document.createElement("iframe");
  frame.title = "PDF capture document"; frame.tabIndex = -1;
  Object.assign(frame.style, { width: "1440px", height: "1400px", border: "0" });
  host.appendChild(frame); document.body.appendChild(host);
  try {
    const doc = frame.contentDocument!;
    const base = doc.createElement("base"); base.href = document.baseURI; doc.head.appendChild(base);
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map(style => {
      const clone = style.cloneNode(true) as HTMLLinkElement;
      const loaded = clone.tagName === "LINK" ? new Promise<void>((resolve, reject) => { clone.onload = () => resolve(); clone.onerror = () => reject(new Error("PDF stylesheet failed")); }) : Promise.resolve();
      doc.head.appendChild(clone); return loaded;
    });
    const root = source.cloneNode(true) as HTMLElement;
    Object.assign(root.style, { width: `${PDF_PAGE_WIDTH}px`, transform: "none", pointerEvents: "none" });
    doc.documentElement.lang = source.lang; doc.documentElement.dir = source.dir;
    doc.body.style.margin = "0"; doc.body.appendChild(root);
    const originalCanvases = source.querySelectorAll("canvas");
    root.querySelectorAll("canvas").forEach((canvas, index) => { canvas.width = originalCanvases[index].width; canvas.height = originalCanvases[index].height; canvas.getContext("2d")?.drawImage(originalCanvases[index], 0, 0); });
    const pages = Object.freeze(Array.from(root.querySelectorAll<HTMLElement>(".pdf-document-page")));
    if (!pages.length) throw new Error("No PDF pages");
    pages.forEach((page, index) => {
      Object.assign(page.style, { width: `${PDF_PAGE_WIDTH}px`, minWidth: `${PDF_PAGE_WIDTH}px`, maxWidth: `${PDF_PAGE_WIDTH}px`, height: `${PDF_PAGE_HEIGHT}px`, minHeight: `${PDF_PAGE_HEIGHT}px`, maxHeight: `${PDF_PAGE_HEIGHT}px`, boxSizing: "border-box" });
      page.querySelectorAll<HTMLElement>("[data-pdf-page-counter]").forEach(counter => { counter.dir = "ltr"; counter.textContent = `${index + 1} / ${pages.length}`; });
    });
    await bounded(Promise.all(styles)); await ready(root); await frameTick();
    let disposed = false;
    let queue: Promise<unknown> = Promise.resolve();
    return { pages, dispose: () => { disposed = true; host.remove(); }, capture: (page, signal) => {
      const job = queue.then(async () => {
        if (disposed || signal?.aborted) throw new Error("Obsolete PDF page render");
        if (!pages.includes(page)) throw new Error("Page is not in this PDF session");
        return bounded(html2canvas(page, { allowTaint: false, useCORS: true, logging: false, backgroundColor: "#ffffff", imageTimeout: 15000,
          scale, width: PDF_PAGE_WIDTH, height: PDF_PAGE_HEIGHT, windowWidth: 1440, windowHeight: PDF_PAGE_HEIGHT, scrollX: 0, scrollY: 0 }), 20000);
      });
      queue = job.catch(() => {}); return job;
    } };
  } catch (error) { host.remove(); throw error; }
}
